// Sugalarm — avatar crop/confirm (cycle 330.6).
//
// A dedicated fork of the cycle-87 `BackgroundCropper`, specialized for
// avatars. The fork is the option the cycle-330 prompt authorizes, and it is
// the right one: an avatar needs the exact OPPOSITE of two properties that
// are load-bearing for a background.
//
//   BackgroundCropper (unchanged, do not touch):
//     * 16:9 frame; can NEVER zoom below "cover" — a wall display must never
//       letterbox.
//   AvatarCropper (this file):
//     * true 1:1 frame with a CIRCULAR mask (the avatar renders round);
//     * CAN zoom below cover, all the way to "fit", so a face near the edge
//       of the source photo can be shrunk to fit entirely inside the circle.
//
// ── The two defects this fixes (both reproduced headlessly first) ──────
//
// 1. THE CIRCLE WAS AN OVAL. `BackgroundCropper`'s `.frame` is a flex item
//    inside `.panel` (a flex column with `max-height: 100%`), and its
//    `flex-shrink` defaults to 1. The component sets a SQUARE height inline
//    (`height = measuredWidth / aspect`), but whenever that square exceeded
//    the panel's available height, flexbox SHRANK it vertically — width
//    held, height collapsed. Measured: kiosk 1024×640 asked 684×684 and
//    rendered 686×462 (ring aspect 1.50); mobile landscape rendered 686×212
//    (aspect 3.39). `border-radius: 50%` on a non-square box is an ellipse.
//    It looked nearly right only in mobile portrait (1.01), which is why it
//    slipped through.
//    FIX: the frame is driven from ONE dimension — `aspect-ratio: 1` +
//    `flex: 0 0 auto` (it can no longer be shrunk) + a `max-width` capped
//    against viewport height so it always fits without being squeezed. No
//    inline height at all. Rendered height therefore ALWAYS equals width.
//
// 2. THE SAME BUG BROKE WYSIWYG. The old math derived `frameH = frameW /
//    aspect` — the height the frame was SUPPOSED to have, not the one it
//    actually rendered at. So cover-scale, clamping, and the export source
//    rect were all computed against a frame taller than reality, and the
//    saved image did not match the preview. Here `frameH === frameW` by
//    construction, and the export draws with the SAME transform the preview
//    uses (below), so what you see is exactly what posts.
//
// Output is UNCHANGED and stays compatible with every existing avatar
// display: an opaque, SQUARE 1:1 JPEG (≤512 long edge). The circle is a
// display mask — `MemberAvatar` renders the square image inside a circle.
// At sub-cover zoom the uncovered area is filled with a solid `--bg-elev`
// token BEFORE the image is drawn, so the JPEG is never transparent (JPEG
// has no alpha — an unfilled canvas would post as black corners).

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { overlayHost } from "../lib/overlayHost";

import {
  clampOffset as clampOffsetPure,
  coverScale as coverScalePure,
  exportSize,
  fitScale as fitScalePure,
} from "../lib/avatarCrop";
import { Button } from "./ui/Button";
import styles from "./AvatarCropper.module.css";

interface Props {
  /** Source image — browser-decodable (HEIC already converted upstream). */
  source: Blob;
  /** Disables Confirm while the export/upload is in flight. */
  busy?: boolean;
  onConfirm: (blob: Blob, width: number, height: number) => void;
  onCancel: () => void;
  /** Long-edge cap (px) of the exported square JPEG. Never upscaled. */
  outputMaxLongEdge?: number;
  title?: string;
  hint?: string;
}

/** Max zoom, as a multiple of the COVER scale (not of fit). */
const MAX_ZOOM = 8;
/** Slider resolution. */
const SLIDER_STEPS = 1000;

export function AvatarCropper({
  source,
  busy = false,
  onConfirm,
  onCancel,
  outputMaxLongEdge = 512,
  title = "Preview · your avatar",
  hint = "Drag to reposition · pinch, scroll, or use the slider to zoom out and fit the whole photo.",
}: Props) {
  const frameRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [srcUrl, setSrcUrl] = useState<string | null>(null);
  const [nat, setNat] = useState<{ w: number; h: number } | null>(null);
  const [frameW, setFrameW] = useState(0);
  const [view, setView] = useState({ s: 1, ox: 0, oy: 0 });
  const [exporting, setExporting] = useState(false);

  // The frame is a TRUE square (CSS `aspect-ratio: 1` + `flex: 0 0 auto`),
  // so its height is its width. This is the whole fix for defect #1 — and
  // because every calculation below uses it, it is also the fix for #2.
  const frameH = frameW;

  useEffect(() => {
    const url = URL.createObjectURL(source);
    setSrcUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [source]);

  // ── Size the square from the ACTUAL available box (cycle 330.7) ──────
  //
  // This is measured in JS rather than expressed in CSS `vh`, because on the
  // KIOSK `vh` is a trap: the kiosk renders a FIXED 1024×640 `.kiosk` box
  // that `KioskFrame` then `transform: scale()`s up to the real panel
  // (1920×1200 on the A9+). Viewport units resolve against the REAL viewport,
  // NOT the scaled stage — so a `52vh` cap computed ~624px of "available
  // height" when the stage only has ~608px in total, the frame ballooned, and
  // the panel overflowed into a SCROLLBAR.
  //
  // Measuring the flex-sized `.frameArea` is scale-agnostic and exact: the
  // square is `min(availableWidth, availableHeight)`, so it always fits and
  // the panel never scrolls. Setting BOTH width and height from one number
  // also makes the frame square by construction (the cycle-330.6 fix), with
  // no reliance on `aspect-ratio` under a transform.
  const areaRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    const measure = () => {
      const side = Math.floor(Math.min(el.clientWidth, el.clientHeight));
      setFrameW(side > 0 ? side : 0);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [srcUrl]);

  // Scale + clamp math lives in `lib/avatarCrop.ts` (pure + unit-tested).
  const coverScale = useCallback(
    () => (nat ? coverScalePure(frameW, nat) : 1),
    [nat, frameW],
  );
  const fitScale = useCallback(
    () => (nat ? fitScalePure(frameW, nat) : 1),
    [nat, frameW],
  );

  const maxScale = useCallback(() => coverScale() * MAX_ZOOM, [coverScale]);

  const clampOffset = useCallback(
    (s: number, ox: number, oy: number) =>
      nat ? clampOffsetPure(frameW, nat, s, ox, oy) : { ox, oy },
    [nat, frameW],
  );

  /** Apply a new scale anchored at a frame-space point. */
  const zoomTo = useCallback(
    (nextRaw: number, anchorX: number, anchorY: number) => {
      setView((v) => {
        const next = Math.min(maxScale(), Math.max(fitScale(), nextRaw));
        const k = next / v.s;
        const ox = anchorX - (anchorX - v.ox) * k;
        const oy = anchorY - (anchorY - v.oy) * k;
        const c = clampOffset(next, ox, oy);
        return { s: next, ox: c.ox, oy: c.oy };
      });
    },
    [clampOffset, fitScale, maxScale],
  );

  // Initialise at COVER (unchanged default — the common case is full-bleed;
  // the user zooms out toward fit only if they want the whole photo).
  useEffect(() => {
    if (!nat || frameW === 0) return;
    const s = coverScale();
    const ox = (frameW - nat.w * s) / 2;
    const oy = (frameH - nat.h * s) / 2;
    const c = clampOffset(s, ox, oy);
    setView({ s, ox: c.ox, oy: c.oy });
  }, [nat, frameW, frameH, coverScale, clampOffset]);

  // ── pan (single pointer) + pinch (two pointers) ─────────────────────
  const pointers = useRef<Map<number, { x: number; y: number }>>(new Map());
  const gesture = useRef<{
    startView: { s: number; ox: number; oy: number };
    startDist: number;
    startMid: { x: number; y: number };
  } | null>(null);

  const onPointerDown = (e: React.PointerEvent) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    gesture.current = null;
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId) || !nat) return;
    const prev = pointers.current.get(e.pointerId)!;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.current.values()];

    if (pts.length === 1) {
      const dx = e.clientX - prev.x;
      const dy = e.clientY - prev.y;
      setView((v) => {
        const c = clampOffset(v.s, v.ox + dx, v.oy + dy);
        return { s: v.s, ox: c.ox, oy: c.oy };
      });
      return;
    }
    if (pts.length >= 2) {
      const a = pts[0];
      const b = pts[1];
      if (!a || !b) return;
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const rect = frameRef.current?.getBoundingClientRect();
      const midX = (a.x + b.x) / 2 - (rect?.left ?? 0);
      const midY = (a.y + b.y) / 2 - (rect?.top ?? 0);
      if (!gesture.current) {
        gesture.current = {
          startView: view,
          startDist: dist || 1,
          startMid: { x: midX, y: midY },
        };
        return;
      }
      const g = gesture.current;
      // Floor is FIT, not cover — pinching in can now shrink past cover.
      zoomTo(g.startView.s * (dist / g.startDist), midX, midY);
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) gesture.current = null;
  };

  const onWheel = (e: React.WheelEvent) => {
    if (!nat) return;
    const rect = frameRef.current?.getBoundingClientRect();
    const px = e.clientX - (rect?.left ?? 0);
    const py = e.clientY - (rect?.top ?? 0);
    zoomTo(view.s * Math.exp(-e.deltaY * 0.0015), px, py);
  };

  // ── zoom slider (the reliable control on kiosk + mobile) ────────────
  // Linear over [fit, max]; anchored at the frame centre so zooming keeps
  // the middle of the circle put.
  const lo = fitScale();
  const hi = maxScale();
  const sliderPos =
    hi > lo ? Math.round(((view.s - lo) / (hi - lo)) * SLIDER_STEPS) : 0;

  const onSlider = (raw: number) => {
    const next = lo + (raw / SLIDER_STEPS) * (hi - lo);
    zoomTo(next, frameW / 2, frameH / 2);
  };

  /** Resolve a CSS token to a concrete color for the canvas fill. */
  const fillColor = (): string => {
    if (typeof window === "undefined") return "#151b23";
    const v = getComputedStyle(document.documentElement)
      .getPropertyValue("--bg-elev")
      .trim();
    return v || "#151b23";
  };

  // ── confirm → canvas export ─────────────────────────────────────────
  const confirm = async () => {
    const img = imgRef.current;
    if (!img || !nat || frameW === 0) return;
    setExporting(true);
    try {
      // Never upscale: the effective source resolution across the frame is
      // frameW / s, so cap the output by it as well as by the 512 ceiling.
      const outW = exportSize(frameW, view.s, outputMaxLongEdge);
      const canvas = document.createElement("canvas");
      canvas.width = outW;
      canvas.height = outW; // square, always
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("no 2d context");

      // Opaque background FIRST. At sub-cover zoom part of the square isn't
      // covered by the photo; JPEG has no alpha, so without this those areas
      // would post as black.
      ctx.fillStyle = fillColor();
      ctx.fillRect(0, 0, outW, outW);

      // Draw with the SAME transform the preview uses, scaled frame→output.
      // This makes the export WYSIWYG by construction rather than by a
      // second, separately-derived source-rect calculation that can drift.
      const k = outW / frameW;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(
        img,
        view.ox * k,
        view.oy * k,
        nat.w * view.s * k,
        nat.h * view.s * k,
      );

      const blob: Blob | null = await new Promise((res) =>
        canvas.toBlob((b) => res(b), "image/jpeg", 0.9),
      );
      if (!blob) throw new Error("export failed");
      onConfirm(blob, outW, outW);
    } finally {
      setExporting(false);
    }
  };

  // ── PORTAL TO <body> (cycle 330.8) ──────────────────────────────────
  //
  // This dialog is `position: fixed; inset: 0`, which SHOULD mean "fill the
  // viewport". It didn't on mobile, and the reason is a CSS containing-block
  // rule that bites hard here: the mobile Settings drill-down pane
  // (`.mobileDetail`) carries `transform: translateX(...)` AND
  // `will-change: transform` for its slide-in. EITHER of those makes the
  // element a containing block for `position: fixed` DESCENDANTS — so the
  // backdrop was sized to the settings pane, not the screen. Result: a tiny
  // crop frame with most of the phone wasted below it.
  //
  // Portalling to <body> escapes the transformed ancestor, so `fixed` means
  // the viewport again and the frame gets the whole screen to size itself
  // against. The KIOSK was always fine (no drill-down transform) — this
  // changes nothing there.
  const dialog = (
    <div
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Crop avatar"
    >
      <div className={styles.panel}>
        <div className={styles.title}>{title}</div>
        <div className={styles.hint}>{hint}</div>
        {/* Flex-sized available box. The square frame below is sized to
            min(its width, its height) in JS, so it always fits and the panel
            never scrolls — on the kiosk's scaled stage as well as on mobile,
            where `vh` is unreliable (URL bar / safe-area insets). */}
        <div className={styles.frameArea} ref={areaRef}>
        <div
          ref={frameRef}
          className={styles.frame}
          style={
            frameW > 0 ? { width: frameW, height: frameW } : { visibility: "hidden" }
          }
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onWheel={onWheel}
        >
          {srcUrl ? (
            <img
              ref={imgRef}
              src={srcUrl}
              alt="Avatar to crop"
              draggable={false}
              onLoad={(e) =>
                setNat({
                  w: e.currentTarget.naturalWidth,
                  h: e.currentTarget.naturalHeight,
                })
              }
              className={styles.img}
              style={{
                transform: `translate(${view.ox}px, ${view.oy}px) scale(${view.s})`,
              }}
            />
          ) : null}
          {/* Cycle 330.7 — the two guides are DESIGNATED, and they mean
              different things:

                • DASHED ring = SAFE ZONE. It is the circle inscribed in the
                  square, i.e. exactly what MemberAvatar shows. Anything
                  outside it is saved in the JPEG but MASKED AWAY on display.
                  Keep what matters inside this line.

                • INNER solid ring = FOCUS. The avatar renders small (24–80px)
                  in the header and on cards; a face centred in here still
                  reads at that size. Put the face here.

              Both are non-interactive overlays. Neither changes the export —
              the saved image is still the whole square. */}
          <div className={styles.circleMask} aria-hidden />
          <div className={styles.safeRing} aria-hidden />
          <div className={styles.focusRing} aria-hidden />
        </div>
        </div>

        {/* Legend — names the two rings so they aren't decoration. */}
        <div className={styles.legend}>
          <span className={styles.legendItem}>
            <span className={styles.swatchSafe} aria-hidden />
            Safe zone — kept in the round avatar
          </span>
          <span className={styles.legendItem}>
            <span className={styles.swatchFocus} aria-hidden />
            Focus — put the face here
          </span>
        </div>

        <div className={styles.zoomRow}>
          <span className={styles.zoomLabel} aria-hidden>
            Fit
          </span>
          <input
            className={styles.zoom}
            type="range"
            min={0}
            max={SLIDER_STEPS}
            step={1}
            value={sliderPos}
            disabled={!nat}
            aria-label="Zoom"
            onChange={(e) => onSlider(Number(e.target.value))}
          />
          <span className={styles.zoomLabel} aria-hidden>
            Zoom
          </span>
        </div>

        <div className={styles.actions}>
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={busy || exporting}
          >
            ↺ Retake
          </Button>
          <Button
            size="sm"
            onClick={confirm}
            disabled={busy || exporting || !nat}
          >
            {busy || exporting ? "Working…" : "Use photo"}
          </Button>
        </div>
      </div>
    </div>
  );

  return typeof document !== "undefined"
    ? createPortal(dialog, overlayHost() ?? document.body)
    : dialog;
}
